import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  listContactSubmissions,
  listContactSubmissionEvents,
  updateContactSubmission,
} from "@/lib/contact-admin.functions";
import {
  ChevronDown,
  Euro,
  ChevronRight,
  Filter,
  LineChart,
  Loader2,
  LogOut,
  Mail,
  Phone,
  MapPin,
  Clock,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";

type StatusFilter = "all" | "nieuw" | "gefilterd" | "afgehandeld";
type SubmissionStatus = "nieuw" | "gefilterd" | "afgehandeld";

export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminPage,
  head: () => ({
    meta: [
      { title: "Beheer — Contactaanvragen | Fedor Timmerwerken" },
      { name: "description", content: "Beheerdersoverzicht van contactaanvragen." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

const STATUS_LABEL: Record<SubmissionStatus, string> = {
  nieuw: "Nieuw",
  gefilterd: "Gefilterd",
  afgehandeld: "Afgehandeld",
};

const STATUS_STYLES: Record<SubmissionStatus, string> = {
  nieuw: "bg-primary/10 text-primary border-primary/30",
  gefilterd: "bg-muted text-muted-foreground border-border",
  afgehandeld: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
};

function StatusPill({ status }: { status: SubmissionStatus }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
        STATUS_STYLES[status],
      )}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function AdminPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const listFn = useServerFn(listContactSubmissions);
  const updateFn = useServerFn(updateContactSubmission);
  const [filter, setFilter] = useState<StatusFilter>("all");

  const list = useQuery({
    queryKey: ["contact-submissions", filter],
    queryFn: () => listFn({ data: { status: filter } }),
  });

  const update = useMutation({
    mutationFn: (input: { id: string; status?: SubmissionStatus; admin_notes?: string | null }) =>
      updateFn({ data: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contact-submissions"] });
      qc.invalidateQueries({ queryKey: ["contact-submission-events"] });
    },
  });

  const signOut = async () => {
    await qc.cancelQueries();
    qc.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  const items = list.data ?? [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      <header className="mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Contactaanvragen</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Overzicht van alle inzendingen met status en volledig logboek.
          </p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/admin-kosten"
            className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-secondary"
          >
            <Euro className="h-4 w-4" /> Prijzen beheren
          </Link>
          <Link
            to="/admin-seo"
            className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-secondary"
          >
            <LineChart className="h-4 w-4" /> SEO monitoring
          </Link>
          <button
            type="button"
            onClick={() => list.refetch()}
            className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-secondary"
          >
            <RefreshCw className={cn("h-4 w-4", list.isFetching && "animate-spin")} />
            Vernieuwen
          </button>
          <button
            type="button"
            onClick={signOut}
            className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-2 text-sm hover:bg-secondary"
          >
            <LogOut className="h-4 w-4" /> Uitloggen
          </button>
        </div>
      </header>

      <div className="mb-5 flex items-center gap-2 overflow-x-auto pb-1">
        <Filter className="h-4 w-4 shrink-0 text-muted-foreground" />
        {(["all", "nieuw", "gefilterd", "afgehandeld"] as const).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={cn(
              "shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              filter === s
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border bg-background hover:bg-secondary",
            )}
          >
            {s === "all" ? "Alles" : STATUS_LABEL[s]}
          </button>
        ))}
        <span className="ml-auto text-xs text-muted-foreground">{items.length} aanvragen</span>
      </div>

      {list.isLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : list.error ? (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          Kon aanvragen niet laden. Controleer of je account beheerdersrechten heeft.
        </p>
      ) : items.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          Geen aanvragen in deze categorie.
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => (
            <SubmissionRow
              key={item.id}
              item={item}
              onUpdate={(patch) => update.mutate({ id: item.id, ...patch })}
              saving={update.isPending && update.variables?.id === item.id}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

type Submission = Awaited<ReturnType<typeof listContactSubmissions>>[number];

function SubmissionRow({
  item,
  onUpdate,
  saving,
}: {
  item: Submission;
  onUpdate: (patch: { status?: SubmissionStatus; admin_notes?: string | null }) => void;
  saving: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(item.admin_notes ?? "");
  const eventsFn = useServerFn(listContactSubmissionEvents);
  const events = useQuery({
    queryKey: ["contact-submission-events", item.id],
    queryFn: () => eventsFn({ data: { submissionId: item.id } }),
    enabled: open,
  });

  const created = new Date(item.created_at);
  const status = item.status as SubmissionStatus;

  return (
    <li className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 p-4 text-left hover:bg-secondary/40"
      >
        {open ? (
          <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">
              {item.voornaam} {item.achternaam}
            </span>
            <StatusPill status={status} />
            <span className="ml-auto text-xs text-muted-foreground">
              {created.toLocaleString("nl-NL", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
          <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">
            {item.onderwerp ? `${item.onderwerp} — ` : ""}
            {item.bericht}
          </p>
        </div>
      </button>

      {open && (
        <div className="border-t border-border p-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1 text-sm">
              <p className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <a href={`mailto:${item.email}`} className="hover:underline">
                  {item.email}
                </a>
              </p>
              {item.telefoon && (
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <a href={`tel:${item.telefoon}`} className="hover:underline">
                    {item.telefoon}
                  </a>
                </p>
              )}
              {item.adres && (
                <p className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  {item.adres}
                </p>
              )}
              {item.onderwerp && (
                <p className="text-muted-foreground">
                  <strong className="text-foreground">Onderwerp:</strong> {item.onderwerp}
                </p>
              )}
            </div>

            <div>
              <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Status
              </label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {(["nieuw", "gefilterd", "afgehandeld"] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={saving || s === status}
                    onClick={() => onUpdate({ status: s })}
                    className={cn(
                      "rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-100",
                      s === status
                        ? cn("cursor-default", STATUS_STYLES[s])
                        : "border-border bg-background hover:bg-secondary",
                    )}
                  >
                    {STATUS_LABEL[s]}
                  </button>
                ))}
              </div>
              {item.handled_at && (
                <p className="mt-2 flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="h-3 w-3" />
                  Afgehandeld op{" "}
                  {new Date(item.handled_at).toLocaleString("nl-NL", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </p>
              )}
            </div>
          </div>

          <div className="mt-4">
            <p className="whitespace-pre-wrap rounded-md border border-border bg-background p-3 text-sm">
              {item.bericht}
            </p>
          </div>

          <div className="mt-4">
            <label htmlFor={`notes-${item.id}`} className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Interne notities
            </label>
            <textarea
              id={`notes-${item.id}`}
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Notities voor jezelf..."
            />
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                disabled={saving || notes === (item.admin_notes ?? "")}
                onClick={() => onUpdate({ admin_notes: notes || null })}
                className="rounded-md border border-input bg-background px-3 py-1.5 text-xs hover:bg-secondary disabled:opacity-50"
              >
                Notities opslaan
              </button>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Logboek
            </h3>
            {events.isLoading ? (
              <Loader2 className="mt-2 h-4 w-4 animate-spin text-muted-foreground" />
            ) : events.data && events.data.length > 0 ? (
              <ol className="mt-2 space-y-2 border-l border-border pl-4">
                {events.data.map((ev) => (
                  <li key={ev.id} className="relative text-xs">
                    <span className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-primary" />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-foreground">
                        {ev.event_type === "created"
                          ? "Ingediend"
                          : ev.event_type === "status_changed"
                            ? `Status: ${ev.from_status ?? "?"} → ${ev.to_status ?? "?"}`
                            : ev.event_type === "note_updated"
                              ? "Notitie bijgewerkt"
                              : ev.event_type}
                      </span>
                      <span className="text-muted-foreground">
                        {new Date(ev.created_at).toLocaleString("nl-NL", {
                          day: "2-digit",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    {ev.note && (
                      <p className="mt-0.5 text-muted-foreground">{ev.note}</p>
                    )}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">Geen gebeurtenissen.</p>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
